import React, { useState, useEffect, useMemo } from "react";
import { IonBadge, IonInput } from "@ionic/react";
import classNames from "classnames/bind";
import { ProductType, Product as TProduct } from "@core/types";
import { useTranslation } from "react-i18next";
import styles from "./VariationBadgesFortime.module.scss";
import { useConfig } from "../../../../../providers/config";
import { UseProductDetailCounterReturn } from "../../../../../hooks/useProductDetailCounter";
import { AttributeSelector } from "./AttributeSelector";
import { debounce } from "lodash";

const cx = classNames.bind(styles);

interface IVariationBadgesProps {
  variations: TProduct[] | undefined;
  onVariationSelect: (variation: TProduct | null) => void;
  product: any;
  isNewProduct: boolean;
  selectedVariation: any;
  counterHook: UseProductDetailCounterReturn;
  onAddToCart?: (product: TProduct) => void;
  onRemoveFromCart?: (productId: string) => void;
  onSetProductQuantity?: (productId: string, quantity: number, product: TProduct) => void;
  cartQuantity: number;
}

const getOnSaleProductPrice = (product: any) => {
  if (!product || !product.regular_price || !product.sale_price) {
    return 0;
  }
  return Math.round(((product.regular_price - product.sale_price) / product.regular_price) * 100);
};

const getVariableProductPrice = (selectedVariation: any) => {
  if (!selectedVariation || !selectedVariation.regular_price || !selectedVariation.sale_price) {
    return 0;
  }
  return Math.round(
    ((selectedVariation.regular_price - selectedVariation.sale_price) / selectedVariation.regular_price) * 100,
  );
};

const VariationBadgesFortime: React.FC<IVariationBadgesProps> = ({
  variations,
  onVariationSelect,
  product,
  isNewProduct = false,
  selectedVariation,
  counterHook,
  onAddToCart,
  onRemoveFromCart,
  onSetProductQuantity,
  cartQuantity,
}) => {
  const { t } = useTranslation();
  const { selectedAttribute, handleAttributeSelect, getCurrentPrice, getMaxQuantity } = counterHook;

  const [inputValue, setInputValue] = useState<number>(cartQuantity);
  const [isUserTyping, setIsUserTyping] = useState<boolean>(false);

  useEffect(() => {
    if (!isUserTyping) {
      setInputValue(cartQuantity);
    }
  }, [cartQuantity, isUserTyping]);

  useEffect(() => {
    setIsUserTyping(false);
  }, [selectedVariation]);

  const debouncedSetQuantity = useMemo(
    () =>
      debounce((productId: string, quantity: number, productData: TProduct) => {
        if (onSetProductQuantity) {
          onSetProductQuantity(productId, quantity, productData);
        }
        setIsUserTyping(false);
      }, 1000),
    [onSetProductQuantity],
  );

  const debouncedResetTyping = useMemo(
    () =>
      debounce(() => {
        setIsUserTyping(false);
      }, 1500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetQuantity.cancel();
      debouncedResetTyping.cancel();
    };
  }, [debouncedSetQuantity, debouncedResetTyping]);

  const addItemToCart = () => {
    if (onAddToCart && product) {
      const productToAdd = product.type === "variable" ? selectedVariation : product;
      if (productToAdd) {
        const unitName = selectedAttribute?.name || "";
        const parentName = productToAdd.type === ProductType.VARIATION ? product?.name : "";
        onAddToCart({ ...productToAdd, unitName, parentName } as TProduct);
      }
    }
  };

  const removeItemFromCart = () => {
    if (onRemoveFromCart && product) {
      const productToRemove = product.type === "variable" ? selectedVariation : product;
      if (productToRemove) {
        onRemoveFromCart(productToRemove.id);
      }
    }
  };

  const getTotalStockQuantity = () => {
    const currentProduct = product?.type === "variable" ? selectedVariation : product;
    return currentProduct?.stockQuantity || 0;
  };

  const handleInputChange = (e: any) => {
    const value = e.detail.value;
    setIsUserTyping(true);
    debouncedResetTyping();

    if (value === "" || value === null) {
      setInputValue(0);
      if (cartQuantity > 0 && onSetProductQuantity) {
        const productToUpdate = product.type === "variable" ? selectedVariation : product;
        if (productToUpdate) {
          const unitName = selectedAttribute?.name || "";
          debouncedSetQuantity(productToUpdate.id, 0, { ...productToUpdate, unitName } as TProduct);
        }
      }
      return;
    }

    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue >= 0) {
      const totalStock = getTotalStockQuantity();

      const clampedValue = Math.min(Math.max(0, numericValue), totalStock);

      setInputValue(clampedValue);

      if (clampedValue !== cartQuantity) {
        const productToUpdate = product.type === "variable" ? selectedVariation : product;
        if (productToUpdate) {
          const unitName = selectedAttribute?.name || "";
          debouncedSetQuantity(productToUpdate.id, clampedValue, { ...productToUpdate, unitName } as TProduct);
        }
      } else {
        setIsUserTyping(false);
      }
    }
  };

  const getUnitName = () => {
    return selectedAttribute ? selectedAttribute.name : "шт.";
  };

  const handleVariationClick = (variation: TProduct) => {
    onVariationSelect(variation);
  };

  const isVariationAvailable = (variation: TProduct) => {
    return variation.stockQuantity > 0;
  };

  const getVariationName = (variation: TProduct) => {
    return variation.name || `Variation ${variation.id}`;
  };

  const showVariationBadges =
    variations && Array.isArray(variations) && variations.length > 0 && product?.type === "variable";

  const sortedVariations = useMemo(() => {
    if (!variations || !Array.isArray(variations)) {
      return [];
    }

    const variationsWithAvailability = variations.map((variation) => ({
      variation,
      isAvailable: isVariationAvailable(variation),
      isSelected: selectedVariation?.id === variation.id,
      name: getVariationName(variation),
    }));

    return variationsWithAvailability.sort((a, b) => {
      if (a.isAvailable && !b.isAvailable) return -1;
      if (!a.isAvailable && b.isAvailable) return 1;
      return 0;
    });
  }, [variations, selectedVariation]);

  const { minLowStockThresholdToShowNumber, lowStockThresholdToShowPercent, showStockQuantity, showStockStatus } =
    useConfig();
  const totalStockQuantity = getTotalStockQuantity();
  const remainingStock = getMaxQuantity();
  const currentStockQuantity = product?.type === "variable" ? selectedVariation?.stockQuantity : product?.stockQuantity;

  const isProductStockLow =
    showStockQuantity &&
    currentStockQuantity &&
    currentStockQuantity > 0 &&
    ((minLowStockThresholdToShowNumber && remainingStock <= minLowStockThresholdToShowNumber) ||
      (lowStockThresholdToShowPercent &&
        currentStockQuantity > 0 &&
        (remainingStock / currentStockQuantity) * 100 <= lowStockThresholdToShowPercent));

  const stockStatus =
    totalStockQuantity <= 0 || remainingStock <= 0 ? "Количество ограничено" : isProductStockLow ? `Доступно` : null;

  const isOutOfStock = totalStockQuantity <= 0;

  const stockStatusText = `Доступно ${Math.floor(remainingStock)} ${selectedVariation?.portionUnit ?? ""}`;

  return (
    <div className={cx("variationBadges")}>
      {isNewProduct && (
        <IonBadge className={cx("badge", "staticBadge")} color="danger">
          {t("New")}
        </IonBadge>
      )}
      {product?.on_sale && product?.type !== "variable" && (
        <IonBadge className={cx("badgeSalePercent", "badge", "staticBadge")}>{getOnSaleProductPrice(product)}</IonBadge>
      )}
      {product?.type === "variable" && selectedVariation?.on_sale && (
        <IonBadge className={cx("badgeSalePercent", "badge", "staticBadge")}>
          {getVariableProductPrice(selectedVariation)}
        </IonBadge>
      )}

      <div className={cx("attributesSection")}>
        {!selectedVariation ? (
          <div className={cx("selectVariationMessage")}>
            <div className={cx("messageIcon")}>📦</div>
            <div className={cx("messageText")}>{t("Please select product variation")}</div>
          </div>
        ) : (
          <div className={cx("attributesContainer")}>
            <AttributeSelector
              selectedVariation={selectedVariation}
              selectedAttribute={selectedAttribute}
              handleAttributeSelect={handleAttributeSelect}
            />

            <div className={cx("largePriceDisplay")}>
              <div className={cx("mainPrice")}>
                {(getCurrentPrice() * cartQuantity).toLocaleString("ru-RU", { minimumFractionDigits: 2 })} ₽
              </div>
            </div>

            <div className={cx("modernQuantityControl")}>
              <button
                className={cx("modernQuantityBtn")}
                onClick={() => {
                  if (cartQuantity > 0) {
                    removeItemFromCart();
                  }
                }}
                disabled={cartQuantity <= 0 || isOutOfStock}
              >
                −
              </button>
              <IonInput
                className={cx("modernQuantityInput")}
                type="number"
                min={0}
                max={getTotalStockQuantity()}
                value={inputValue}
                disabled={isOutOfStock}
                onIonChange={handleInputChange}
                onKeyDown={(e) => {
                  const target = e.target as HTMLInputElement;

                  const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"];
                  if (allowedKeys.includes(e.key)) {
                    return;
                  }

                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault();
                    return;
                  }

                  if (target.value.length >= 4) {
                    e.preventDefault();
                    return;
                  }
                }}
              />
              <button
                className={cx("modernQuantityBtn")}
                onClick={() => {
                  if (cartQuantity < getTotalStockQuantity()) {
                    addItemToCart();
                  }
                }}
                disabled={cartQuantity >= getTotalStockQuantity() || isOutOfStock}
              >
                +
              </button>
            </div>
            <div className={cx("productInfoSection")}>
              <div className={cx("infoLine")}>
                <span className={cx("infoLabel")}>Цена:</span>
                <span className={cx("infoValue")}>
                  {getCurrentPrice().toFixed(2)} ₽ / {getUnitName()}
                </span>
              </div>
            </div>

            {showStockStatus && stockStatus !== null && (
              <div className={cx("flex items-center justify-between my-2 min-h-6")}>
                <span className="text-sm text-gray-500">{stockStatus}</span>
                {remainingStock > 0 && <span className="text-[#1a1a1a] text-xs font-medium">{stockStatusText}</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {showVariationBadges && (
        <div className={cx("variationContainer")}>
          <div className={cx("variationLabel")}>{t("Choose Variation")}</div>
          <div className={cx("variationList")}>
            {sortedVariations.map(({ variation, isAvailable, isSelected, name }) => (
              <div
                key={variation.id}
                className={cx("variationItem", isSelected && "selected", !isAvailable && "disabled")}
                onClick={() => {
                  if (isAvailable) {
                    handleVariationClick(variation);
                  }
                }}
              >
                <div className={cx("variationName")}>{name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { VariationBadgesFortime };
